import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, isSupported } from 'firebase/messaging';
import { createContext, useEffect, useMemo, useState } from 'react';
import { fbAppConfigs } from '../../../project/appConfigs';
import { _projectAppNames, getAppInfo } from '../../../project/appConfiguration';
import { _firebaseAppTypes, getFirebaseAlts, getFirebaseAuth, getFirebaseDb, getFirebaseFunctions, getFirebaseProds, getFirebaseStorage, getRealtimeDb } from '../../firestoreData/firebaseServices';
import { getCurrentEnvironment } from '../../redirection/current';

export const AppContext = createContext();

/**
 * 
 * @param {*} param0 
 * @returns app_state: main info for the app
 */
const AppProvider = ({ children }) => {

  const [mainMessaging, setMainMessaging] = useState(null);

  const appInfo = getAppInfo(true)
  const { appName } = appInfo ?? {}

  // Determine environment
  let _environment = 'dev';
  if (appName === _projectAppNames.meetingevolution) {
    _environment = document.location.host === 'mobile.events.meetingevolution.net' ? 'prod' : 'dev';
  }
  // --- Initialize PRIMARY app ---
  const primaryConfig = fbAppConfigs[appName][_environment];

  const _fsKey = appName + '_' + _environment

  const mainApp = getApps().some(app => app.name === appName)
    ? getApp(appName)
    : initializeApp(primaryConfig, _fsKey);

  const mainAuth = useMemo(() => getFirebaseAuth(), []);
  const mainDb = useMemo(() => getFirebaseDb(), []);
  const mainRt = useMemo(() => getRealtimeDb(), []);
  const mainFunctions = useMemo(() => getFirebaseFunctions(), []);
  const mainStorage = useMemo(() => getFirebaseStorage(), []);

  const altApp = useMemo(() => getFirebaseAlts(_firebaseAppTypes.app), []);
  const altAuth = useMemo(() => getFirebaseAlts(_firebaseAppTypes.auth), []);
  const altDb = useMemo(() => getFirebaseAlts(_firebaseAppTypes.database), []);
  const altFunctions = useMemo(() => getFirebaseAlts(_firebaseAppTypes.functions), []);
  const altStorage = useMemo(() => getFirebaseAlts(_firebaseAppTypes.storage), []);
  const altRt = useMemo(() => getFirebaseAlts(_firebaseAppTypes.realtime), []);

  const prodApp = useMemo(() => getFirebaseProds(_firebaseAppTypes.app), []);
  const prodAuth = useMemo(() => getFirebaseProds(_firebaseAppTypes.auth), []);
  const prodDb = useMemo(() => getFirebaseProds(_firebaseAppTypes.database), []);
  const prodFunctions = useMemo(() => getFirebaseProds(_firebaseAppTypes.functions), []);
  const prodStorage = useMemo(() => getFirebaseProds(_firebaseAppTypes.storage), []);
  const prodRt = useMemo(() => getFirebaseProds(_firebaseAppTypes.realtime), []);

  const app_mains = {
    mainApp,
    mainAuth,
    mainDb,
    mainRt,
    mainFunctions,
    mainStorage
  }

  const app_alts = {
    altApp,
    altAuth,
    altDb,
    altRt,
    altFunctions,
    altStorage
  }

  const app_prods = {
    prodApp,
    prodAuth,
    prodDb,
    prodRt,
    prodFunctions,
    prodStorage
  }

  const getServiceMeta = (service, label) => {
    if (!service?.app) return null;
    const { name, options } = service.app;
    return {
      service: label,
      appName: name,
      projectId: options?.projectId ?? null,
      // appId: options?.appId ?? null,
      // apiKey: options?.apiKey ?? null,
      // authDomain: options?.authDomain ?? null,
      databaseURL: options?.databaseURL ?? null,
      // storageBucket: options?.storageBucket ?? null,
      // messagingSenderId: options?.messagingSenderId ?? null,
    };
  };

  // Usage with your state
  const app_metas = {
    auth: getServiceMeta(mainAuth, "auth"),
    firestore: getServiceMeta(mainDb, "firestore"),
    rtdb: getServiceMeta(mainRt, "realtime"),
    functions: getServiceMeta(mainFunctions, "functions"),
    storage: getServiceMeta(mainStorage, "storage"),
    messaging: getServiceMeta(mainMessaging?.firebaseDependencies, "messaging"),
  };

  const isSmartPhone = useMemo(() => {
    const isSmallScreen = window.matchMedia("(max-width: 767px)").matches;
    const ua = navigator.userAgent.toLowerCase();
    const isPhone = /iphone|android.*mobile|windows phone/.test(ua);
    const isTablet = /ipad|android(?!.*mobile)/.test(ua);

    if (isPhone) return true;
    if (isTablet) return true;
    if (isSmallScreen) return false;
    return false;
  })

  // --- Messaging support check ---
  useEffect(() => {
    isSupported().then((supported) => {
      if (supported) {
        const messagingInstance = getMessaging(mainApp);
        setMainMessaging(messagingInstance);
      } else {
        console.warn('Firebase Messaging not supported in this environment.');
      }
    });
  }, [mainApp]);

  return (
    <AppContext.Provider
      value={{
        app_state: {
          currentEnvironment: getCurrentEnvironment(),
          app_metas,
          app_alts,
          app_mains,
          app_prods,
          appInfo,
          appName,
          mainApp,
          mainAuth,
          mainDb,
          mainFunctions,
          mainMessaging,
          mainRt,
          mainStorage,
          isSmartPhone,
        }
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider; 